import React, { useState } from 'react';
import api from '../../services/apiService';

function UnreconciledTransactions({ entries, players, onUpdate, className = '' }) {
    const [selectedPlayers, setSelectedPlayers] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [processedEntries, setProcessedEntries] = useState([]);

    if (!entries || entries.length === 0) return null;

    // Filter out processed entries
    const remainingEntries = entries.filter((_, index) => !processedEntries.includes(index));

    const handlePlayerSelect = (entryIndex, playerId) => {
        setSelectedPlayers(prev => ({
            ...prev,
            [entryIndex]: playerId
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            const updatesData = entries.map((entry, index) => ({
                description: entry.description,
                playerId: selectedPlayers[index],
                date: entry.date,
                amount: entry.amount
            })).filter(update => update.playerId);

            if (updatesData.length === 0) {
                setError('Please select players for the transactions you want to update');
                return;
            }

            const response = await api.post(
                `${process.env.REACT_APP_API_URL}/update_transactions.php`,
                { updates: updatesData }
            );

            if (response.data.success) {
                // Mark entries as processed
                const newProcessedEntries = Object.keys(selectedPlayers).map(Number);
                setProcessedEntries(prev => [...prev, ...newProcessedEntries]);
                
                // Clear selections for processed entries
                setSelectedPlayers({});
                
                // Show success message
                setSuccessMessage(response.data.message);
                
                // Clear success message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);

                // If all entries are processed, trigger parent update
                if (newProcessedEntries.length === entries.length) {
                    onUpdate();
                }
            } else {
                setError(response.data.message || 'Failed to update transactions');
            }
        } catch (error) {
            setError('An error occurred while updating transactions');
            console.error('Update error:', error);
        } finally {
            setLoading(false);
        }
    };

    // If all entries have been processed, return null
    if (remainingEntries.length === 0) return null;

    return (
        <div className={`unreconciled-transactions ${className}`}>
            <h3>Unreconciled Transactions</h3>
            {successMessage && (
                <div className="success-message">
                    {successMessage}
                </div>
            )}
            <div className="transactions-grid">
                <div className="grid-header">
                    <span>Row</span>
                    <span>Date</span>
                    <span>Description</span>
                    <span>Amount</span>
                    <span>Assign To Player</span>
                </div>
                {remainingEntries.map((entry, index) => (
                    <div key={index} className="transaction-row">
                        <span>{entry.row}</span>
                        <span>{entry.date}</span>
                        <span>{entry.description}</span>
                        <span className={entry.multiple ? 'multiple-transactions' : ''} 
                              title={entry.tooltip}>
                            £{Number(entry.amount).toFixed(2)}
                            {entry.multiple && <span className="multiple-indicator">*</span>}
                        </span>
                        <span>
                            <select
                                value={selectedPlayers[index] || ''}
                                onChange={(e) => handlePlayerSelect(index, e.target.value)}
                                className="player-select"
                            >
                                <option value="">Select Player...</option>
                                {players.map(player => (
                                    <option key={player.id} value={player.id}>
                                        {player.name}
                                    </option>
                                ))}
                            </select>
                        </span>
                    </div>
                ))}
            </div>
            {error && <div className="error-message">{error}</div>}
            <div className="actions">
                <button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="submit-button"
                >
                    {loading ? 'Updating...' : 'Update Transactions'}
                </button>
            </div>
        </div>
    );
}

export default UnreconciledTransactions; 